// Pure helpers for the hero's probe strip (probe-blips.tsx): where a check
// location sits in the strip, which side its label opens on, when the next
// marker comes and which location is next.

import type { CheckLocation } from '@/lib/locations'

// The visible map extent: latitude 70 (top) to -50 (bottom), longitude -170 to 180.
const LAT_TOP = 70
const LAT_BOTTOM = -50
const LON_LEFT = -170
const LON_RIGHT = 180

const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

/** Equirectangular position of a point in the map, 0..1 from the top left. */
export function project(lat: number, lon: number): { x: number; y: number } {
  return {
    x: clamp01((lon - LON_LEFT) / (LON_RIGHT - LON_LEFT)),
    y: clamp01((LAT_TOP - lat) / (LAT_TOP - LAT_BOTTOM)),
  }
}

/** Wait before the next marker: 2 to 4 seconds. */
export function nextDelayMs(rand: () => number): number {
  return 2000 + Math.floor(rand() * 2000)
}

// The strip: the map squeezed into one slim row. x runs over the full width
// with an 8 px inset, y over the height with a margin so a label fits.
export const STRIP_INSET = 8
export const STRIP_PAD_Y = 14

/** A location's point in a strip of width x height px, from its top left. */
export function stripPoint(lat: number, lon: number, width: number, height: number): { x: number; y: number } {
  const p = project(lat, lon)
  return {
    x: STRIP_INSET + p.x * (width - 2 * STRIP_INSET),
    y: STRIP_PAD_Y + p.y * (height - 2 * STRIP_PAD_Y),
  }
}

// The widest label, from the dot to the end of its text. Phones show flag and
// city ("Rio de Janeiro"); a strip of WIDE_FROM px or more adds the country
// ("Johannesburg, South Africa").
export const LABEL_W = 150
export const LABEL_W_WIDE = 240
export const WIDE_FROM = 640

/**
 * Which side of the dot the label opens on, so it never leaves the strip. When
 * it fits on neither side (large text on a narrow phone), the side with more room.
 */
export function labelSide(x: number, width: number, labelW = LABEL_W): 'left' | 'right' {
  if (x + labelW <= width) return 'right'
  if (x - labelW >= 0) return 'left'
  return x > width - x ? 'left' : 'right'
}

/** One of the other locations, never the previous one, so no tick is skipped. */
export function pickLocation({
  locations,
  previousCode,
  rand,
}: {
  locations: readonly CheckLocation[]
  previousCode: string | null
  rand: () => number
}): CheckLocation {
  const others = locations.filter((l) => l.code !== previousCode)
  return others[Math.min(others.length - 1, Math.floor(rand() * others.length))]
}

/** Two-letter country code from a flag emoji (two regional indicator symbols). */
export function countryCodeOf(flag: string): string {
  return Array.from(flag, (c) => String.fromCharCode((c.codePointAt(0) ?? 0) - 0x1f1e6 + 65)).join('')
}

/**
 * True when the browser draws a flag emoji as one glyph. Windows draws the two
 * regional indicator symbols as two letters, about twice as wide as one.
 */
export function flagIsOneGlyph(measure: (text: string) => number, flag: string): boolean {
  const one = measure(Array.from(flag)[0])
  return one <= 0 || measure(flag) < one * 1.5
}
